import React, { useEffect, useState, useRef } from 'react';
import OneSignal from 'react-onesignal';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './OneSignalInit.module.css';

// Aggiunge un timeout a qualsiasi Promise: se non si risolve entro `ms` millisecondi,
// rigetta con un errore chiaro invece di bloccare indefinitamente (es. AdBlocker).
const withTimeout = (promise, ms, label) =>
    Promise.race([
        promise,
        new Promise((_, reject) =>
            setTimeout(() => reject(new Error(`[OneSignal] Timeout (${ms}ms): ${label}`)), ms)
        ),
    ]);

export default function OneSignalInit({ user }) {
    const [showSoftPrompt, setShowSoftPrompt] = useState(false);

    // useRef invece di useState: aggiornare questo flag non causa re-render
    // e non finisce nelle dipendenze di useEffect, eliminando il doppio avvio.
    const initializedRef = useRef(false);
    const loggedInUidRef = useRef(null);

    useEffect(() => {
        // Flag per evitare setState su componente già smontato
        let cancelled = false;
        let softPromptTimeout = null;

        const initOneSignal = async () => {
            console.time('[OneSignal] avvio totale');
            try {
                if (!initializedRef.current) {
                    console.time('[OneSignal] init SDK');
                    await withTimeout(
                        OneSignal.init({
                            appId: process.env.REACT_APP_ONESIGNAL_APP_ID || "INSERT_YOUR_APP_ID_IF_VAR_NOT_FOUND",
                            // Necessario per Safari su macOS (Web Push via APNs)
                            safari_web_id: process.env.REACT_APP_ONESIGNAL_SAFARI_WEB_ID || "web.onesignal.auto.1b4e40e7-0d3a-47c7-b9a8-378d25a10892",
                            // Disabilitato: usiamo il nostro soft-prompt custom (modale React)
                            notifyButton: { enable: false },
                            allowLocalhostAsSecureOrigin: true,
                        }),
                        5000,
                        'OneSignal.init()'
                    );
                    console.timeEnd('[OneSignal] init SDK');
                    initializedRef.current = true;
                }

                if (user && user.uid) {
                    let currentExternalId = null;
                    try {
                        if (OneSignal.User?.externalId !== undefined) {
                            currentExternalId = OneSignal.User.externalId;
                        } else if (typeof OneSignal.getExternalUserId === 'function') {
                            currentExternalId = await withTimeout(
                                OneSignal.getExternalUserId(),
                                3000,
                                'getExternalUserId'
                            );
                        }
                    } catch (_) {
                        // Non critico: se la lettura fallisce si procede col login
                    }

                    const alreadyLoggedIn =
                        loggedInUidRef.current === user.uid || currentExternalId === user.uid;

                    if (alreadyLoggedIn) {
                        console.log("[OneSignal] Sessione già valida per:", user.uid, "— login saltato.");
                    } else {
                        console.time('[OneSignal] login utente');
                        console.log("[OneSignal] Login utente:", user.uid, "(precedente SDK:", currentExternalId ?? loggedInUidRef.current ?? "nessuno", ")");
                        if (typeof OneSignal.login === 'function') {
                            await withTimeout(OneSignal.login(user.uid), 5000, 'OneSignal.login()');
                        } else if (typeof OneSignal.setExternalUserId === 'function') {
                            await withTimeout(OneSignal.setExternalUserId(user.uid), 5000, 'setExternalUserId');
                        }
                        loggedInUidRef.current = user.uid;
                        console.timeEnd('[OneSignal] login utente');

                        // Verifica post-login: controlla se la subscription è effettivamente attiva.
                        // Se non lo è ma il permesso è già concesso, significa che l'UID è stato
                        // associato ma la push subscription non è collegata → le notifiche non arriveranno.
                        const pushSubPost = OneSignal.User?.PushSubscription;
                        if (pushSubPost) {
                            console.log(`[OneSignal] Post-login → optedIn: ${pushSubPost.optedIn}, id: ${pushSubPost.id ?? 'null'}`);
                            if (!pushSubPost.optedIn && window.Notification?.permission === 'granted') {
                                console.warn('[OneSignal] UID linkato ma subscription NON attiva con permesso già concesso — questo utente NON riceverà notifiche push.');
                            }
                        }
                    }

                    // Re-opt-in: se il browser ha già il permesso concesso ma l'utente OneSignal
                    // risulta "not subscribed" (es. dopo cancellazione manuale dal dashboard),
                    // chiamiamo optIn() per ri-agganciare la push subscription esistente senza
                    // dover chiedere di nuovo il permesso all'utente.
                    try {
                        const permissionGranted = window.Notification?.permission === 'granted';
                        const pushSub = OneSignal.User?.PushSubscription;
                        const isOptedIn = pushSub?.optedIn;

                        if (permissionGranted && pushSub && !isOptedIn) {
                            console.log("[OneSignal] Permesso già concesso ma subscription inattiva — re-opt-in.");
                            await withTimeout(pushSub.optIn(), 5000, 'PushSubscription.optIn()');
                            console.log("[OneSignal] Re-opt-in completato.");
                        }
                    } catch (optInErr) {
                        console.warn("[OneSignal] Re-opt-in fallito (non bloccante):", optInErr.message);
                    }

                    if (user.ruolo) {
                        const ADMIN_ROLES = ['Admin', 'Sviluppatore'];
                        const tagValue = ADMIN_ROLES.includes(user.ruolo) ? 'admin' : user.ruolo.toLowerCase();

                        const addTagPromise =
                            OneSignal.User && typeof OneSignal.User.addTag === 'function'
                                ? OneSignal.User.addTag("role", tagValue)
                                : typeof OneSignal.sendTag === 'function'
                                    ? OneSignal.sendTag("role", tagValue)
                                    : Promise.resolve();

                        // Timeout sul tag: non è bloccante, se fallisce l'app prosegue
                        await withTimeout(addTagPromise, 3000, 'addTag').catch(() => {});
                    }
                }

                if ('clearAppBadge' in navigator) {
                    navigator.clearAppBadge().catch((error) => console.error("Badge clear error", error));
                }

                // Check tramite API nativa del browser per mostrare il banner solo se mai risposto.
                // Se l'utente ha già declinato di recente (< 3 giorni), non mostrare di nuovo.
                if (!cancelled && window.Notification && window.Notification.permission === 'default') {
                    const SNOOZE_KEY = 'onesignal_soft_prompt_snoozed_at';
                    const SNOOZE_DURATION_MS = 3 * 24 * 60 * 60 * 1000; // 3 giorni
                    const snoozedAt = localStorage.getItem(SNOOZE_KEY);
                    const isSnoozed = snoozedAt && (Date.now() - parseInt(snoozedAt, 10)) < SNOOZE_DURATION_MS;
                    if (!isSnoozed) {
                        softPromptTimeout = setTimeout(() => {
                            if (!cancelled) setShowSoftPrompt(true);
                        }, 1500);
                    }
                }
            } catch (error) {
                // Distingue timeout da errori generici per facilitare il debug
                if (error.message?.startsWith('[OneSignal] Timeout')) {
                    console.warn(error.message, '— OneSignal non disponibile (AdBlocker?). L\'app continua normalmente.');
                } else {
                    console.error("[OneSignal] Errore inizializzazione:", error);
                }
            } finally {
                console.timeEnd('[OneSignal] avvio totale');
            }
        };

        initOneSignal();

        // Cleanup: cancella il timeout del soft-prompt se il componente si smonta
        return () => {
            cancelled = true;
            if (softPromptTimeout) clearTimeout(softPromptTimeout);
        };
    }, [user]); // 'initialized' rimosso: ora è un ref, non causa re-render né doppi avvii

    // Effetto separato: aggiorna il tag "role" in OneSignal quando il ruolo dell'utente cambia
    // (es. admin revocato → tag aggiornato senza dover fare logout/login)
    useEffect(() => {
        if (!user?.ruolo || !initializedRef.current) return;
        const ADMIN_ROLES = ['Admin', 'Sviluppatore'];
        const tagValue = ADMIN_ROLES.includes(user.ruolo) ? 'admin' : user.ruolo.toLowerCase();
        const addTagPromise =
            OneSignal.User && typeof OneSignal.User.addTag === 'function'
                ? OneSignal.User.addTag('role', tagValue)
                : typeof OneSignal.sendTag === 'function'
                    ? OneSignal.sendTag('role', tagValue)
                    : Promise.resolve();
        addTagPromise.catch(e =>
            console.warn('[OneSignal] Aggiornamento tag ruolo fallito (non bloccante):', e?.message)
        );
    }, [user?.ruolo]);

    const handleSoftPromptAccept = async () => {
        setShowSoftPrompt(false);
        try {
            // Compatibilità v16 vs v15 per la richiesta permessi
            if (OneSignal.Notifications && typeof OneSignal.Notifications.requestPermission === 'function') {
                await OneSignal.Notifications.requestPermission();
            } else if (typeof OneSignal.showNativePrompt === 'function') {
                await OneSignal.showNativePrompt();
            } else if (typeof OneSignal.registerForPushNotifications === 'function') {
                await OneSignal.registerForPushNotifications();
            }
        } catch (error) {
            console.error("Errore richiesta permessi:", error);
        }
    };

    const handleSoftPromptDecline = () => {
        localStorage.setItem('onesignal_soft_prompt_snoozed_at', Date.now().toString());
        setShowSoftPrompt(false);
    };

    return (
        <AnimatePresence>
            {showSoftPrompt && (
                <div className={styles.overlay}>
                    <motion.div
                        className={styles.modal}
                        initial={{ scale: 0.9, opacity: 0, y: 50 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 50 }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    >
                        <div className={styles.header}>
                            <div className={styles.iconWrapper}>
                                <span className={styles.icon}>🔔</span>
                            </div>
                            <h3 className={styles.title}>Resta Aggiornato!</h3>
                        </div>

                        <div className={styles.body}>
                            <p className={styles.description}>
                                Attiva le notifiche push per sfruttare al meglio la tua App Palestra.
                            </p>

                            <div className={styles.features}>
                                <div className={styles.feature}>
                                    <span className={styles.featureIcon}>✅</span>
                                    <span>Conferma approvazione account</span>
                                </div>
                                <div className={styles.feature}>
                                    <span className={styles.featureIcon}>✅</span>
                                    <span>Avvisi su allenamenti e iscrizioni</span>
                                </div>
                            </div>
                        </div>

                        <div className={styles.actions}>
                            <button
                                onClick={handleSoftPromptAccept}
                                className={styles.btnAccept}
                            >
                                Consenti Notifiche
                            </button>
                            <button
                                onClick={handleSoftPromptDecline}
                                className={styles.btnDecline}
                            >
                                Forse più tardi
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
